// ─────────────────────────────────────────────────────────────
// API Endpoints Registry
//
// Every route path in the app lives here.
// When a service is renamed, versioned, or restructured,
// you update this file — nothing else changes.
//
// Convention:
//   ENDPOINTS.<service>.<operation> = path string or function
//   Path functions take typed params to avoid string typos.
// ─────────────────────────────────────────────────────────────

export const ENDPOINTS = {

  auth: {
    google:          '/auth/google',
    googleCallback:  '/auth/google/callback',
    microsoft:         '/auth/microsoft',
    microsoftCallback: '/auth/microsoft/callback',
    exchangeCode:    '/auth/exchange-code',
    refresh:         '/auth/refresh',
    logout:          '/auth/logout',
    logoutAll:       '/auth/logout-all',
    me:              '/auth/me',
  },

  discussions: {
    list:            '/discussions',
    create:          '/discussions',
    get:             (id: string) => `/discussions/${id}`,
    update:          (id: string) => `/discussions/${id}`,
    delete:          (id: string) => `/discussions/${id}`,
    vote:            (id: string) => `/discussions/${id}/vote`,
    pin:             (id: string) => `/discussions/${id}/pin`,
    follow:          (id: string) => `/discussions/${id}/follow`,
    unfollow:        (id: string) => `/discussions/${id}/follow`,
    summarise:       (id: string) => `/discussions/${id}/summarise`,
    related:         (id: string) => `/discussions/${id}/related`,
    invokeAgent:     (id: string) => `/discussions/${id}/invoke-agent`,
    suggestedAgents: (id: string) => `/discussions/${id}/suggested-agents`,
    following:       '/discussions/following',
    foryou:          '/discussions/foryou',
    tags:            '/tags/trending',
    categories:      '/categories',
  },

  comments: {
    list:     (discussionId: string) => `/discussions/${discussionId}/comments`,
    create:   (discussionId: string) => `/discussions/${discussionId}/comments`,
    update:   (id: string) => `/comments/${id}`,
    delete:   (id: string) => `/comments/${id}`,
    liked:    '/comments/liked',
    voted:    '/comments/voted',
    like:     (id: string) => `/comments/${id}/like`,
    likes:    (id: string) => `/comments/${id}/likes`,
    vote:     (id: string) => `/comments/${id}/vote`,
    replies:  (id: string) => `/comments/${id}/replies`,
    feedback:    (id: string) => `/comments/${id}/feedback`,
    createOrder: (id: string) => `/comments/${id}/unlock/order`,
    unlock:      (id: string) => `/comments/${id}/unlock`,
    unlockInvoices:        '/me/comment-unlock-invoices',
    unlockInvoiceDownload: (invoiceId: string) => `/me/comment-unlock-invoices/${invoiceId}/download`,
  },

  users: {
    get:             (id: string) => `/users/${id}`,
    me:              '/users/me',
    updateProfile:   '/users/me',
    deleteMe:        '/users/me',
    aiUsage:         '/users/me/ai-usage',
    aiUsageRange:    (from: string, to: string) => `/users/me/ai-usage?from=${from}&to=${to}`,
    aiUsageAgents:   (from: string, to: string) => `/users/me/ai-usage/agents?from=${from}&to=${to}`,
    aiUsageAgentsCreated: (from: string, to: string) => `/users/me/ai-usage/agents/created?from=${from}&to=${to}`,
    preferences:     '/users/me/preferences',
    search:          '/users/search',
    mentionSuggest:  '/users/mention-suggest',
    followUser:      (id: string) => `/users/${id}/follow`,
    unfollowUser:    (id: string) => `/users/${id}/follow`,
    followStatus:    (id: string) => `/users/${id}/follow-status`,
    followers:       (id: string) => `/users/${id}/followers`,
    following:       (id: string) => `/users/${id}/following`,
    followingUsers:  '/users/following/users',
    activity:        (id: string) => `/users/${id}/activity`,
    activityDay:     (id: string, date: string) => `/users/${id}/activity/${date}`,
    onThisDay:       (id: string) => `/users/${id}/on-this-day`,
    weeklySummary:   (id: string) => `/users/${id}/weekly-summary`,
    similarUsers:    (id: string) => `/users/${id}/similar`,
    aiConversations: (id: string) => `/users/${id}/ai-conversations`,
    stats:           (id: string) => `/users/${id}/stats`,
    writingStats:    (id: string) => `/users/${id}/writing-stats`,
    // Admin
    adminList:       '/admin/users',
    adminSearch:     '/admin/users/search',
    addRole:         (id: string) => `/admin/users/${id}/roles/add`,
    removeRole:      (id: string) => `/admin/users/${id}/roles/remove`,
    adminAiUsage:    '/admin/ai-usage',
    adminUserAiUsage: (id: string) => `/admin/users/${id}/ai-usage`,
  },

  notifications: {
    list:     '/notifications',
    activity: '/activity',
    markRead: (id: string) => `/notifications/${id}/read`,
    markAll:  '/notifications/read-all',
    delete:   (id: string) => `/notifications/${id}`,
  },

  leaderboard: {
    list:   '/leaderboard',
    myRank: '/leaderboard/my-rank',
  },

  ai: {
    // Public
    agents:          '/agents',
    topAgents:       '/agents/top',
    stats:           '/stats',
    classify:        '/ai/classify',
    // Admin
    topContributors: '/admin/top-contributors',
    feedbackStats:   '/admin/feedback-stats',
    costStats:       '/admin/cost-stats',
    adminAgents:     '/admin/agents',
    adminAgent:      (id: string) => `/admin/agents/${id}`,
    adminCategories: '/admin/categories',
    adminCategory:   (id: string) => `/admin/categories/${id}`,
    spendingCapConfig: '/admin/config/spending-cap',
    modelPricing:    '/admin/config/model-pricing',
    agentSafetyConfig: '/admin/config/agent-safety',
    knowledgeFiles:  (agentId: string) => `/agents/${agentId}/knowledge`,
    knowledgeFile:   (agentId: string, fileId: string) => `/agents/${agentId}/knowledge/${fileId}`,
    cacheKey:        (key: string) => `/admin/cache/${encodeURIComponent(key)}`,
    cacheAll:        '/admin/cache',
  },

  // Hosted (RAG) agent subscription — real Razorpay order + confirm.
  agentSubscriptions: {
    plans:        (agentId: string) => `/agents/${agentId}/subscription-plans`,
    subscription: (agentId: string) => `/agents/${agentId}/subscription`,
    order:        (agentId: string) => `/agents/${agentId}/subscription/order`,
    confirm:      (agentId: string) => `/agents/${agentId}/subscription/confirm`,
    invoices:     (agentId: string) => `/agents/${agentId}/subscription/invoices`,
    invoiceDownload: (agentId: string, invoiceId: string) => `/agents/${agentId}/subscription/invoices/${invoiceId}/download`,
    myInvoices:      '/me/subscription-invoices',
    myInvoiceDownload: (invoiceId: string) => `/me/subscription-invoices/${invoiceId}/download`,
  },

  developer: {
    apply:               '/developer/apply',
    me:                  '/developer/me',
    hostedProviders:     '/developer/hosted-providers',
    checkAgentName:      '/developer/agents/check-name',
    createAgent:         '/developer/agents',
    finalizeAgent:       (id: string) => `/developer/agents/${id}/finalize`,
    discardDraftAgent:   (id: string) => `/developer/agents/${id}/draft`,
    updateAgent:         (id: string) => `/developer/agents/${id}`,
    deleteAgent:         (id: string) => `/developer/agents/${id}`,
    resubmitAgent:       (id: string) => `/developer/agents/${id}/resubmit`,
    regenerateSecret:    (id: string) => `/developer/agents/${id}/regenerate-secret`,
    agentHealth:         (id: string) => `/developer/agents/${id}/health`,
    agentStats:          (id: string) => `/developer/agents/${id}/stats`,
    agentUsage:          (id: string) => `/developer/agents/${id}/usage`,
    agentInvocations:    (id: string) => `/developer/agents/${id}/invocations`,
    agentInvocation:     (id: string, invId: string) => `/developer/agents/${id}/invocations/${invId}`,
    replayInvocation:    (id: string, invId: string) => `/developer/agents/${id}/invocations/${invId}/replay`,
    sandboxDispatch:     (id: string) => `/developer/agents/${id}/sandbox-dispatch`,
    testWebhook:         (id: string) => `/developer/agents/${id}/test-webhook`,
    apiKeys:             '/developer/api-keys',
    revokeApiKey:        (id: string) => `/developer/api-keys/${id}`,
    agentResponse:       '/api/v1/agent-response',
    earnings:            '/developer/earnings',
    bankDetails:         '/developer/bank-details',
    messageUnlockInvoices:         (id: string) => `/developer/agents/${id}/message-unlock-invoices`,
    messageUnlockInvoiceDownload:  (id: string, invoiceId: string) => `/developer/agents/${id}/message-unlock-invoices/${invoiceId}/download`,
    // Admin
    adminApplications:   '/admin/developer-applications',
    adminReviewApp:      (id: string) => `/admin/developer-applications/${id}/review`,
    adminExternalAgents: '/admin/external-agents',
    adminExternalAgent:  (id: string) => `/admin/external-agents/${id}`,
    adminReviewAgent:    (id: string) => `/admin/external-agents/${id}/review`,
    adminAgentHealth:    (id: string) => `/admin/external-agents/${id}/health`,
    adminSandbox:        (id: string) => `/admin/external-agents/${id}/sandbox-dispatch`,
    adminInvocation:     (id: string, invId: string) => `/admin/external-agents/${id}/invocations/${invId}`,
    adminMatchingDiscs:  (id: string) => `/admin/external-agents/${id}/matching-discussions`,
  },

  moderation: {
    reasons:      '/reports/reasons',
    report:       '/reports',
    adminReasons: '/admin/reports/reasons',
    adminList:    '/admin/reports',
    adminStats:   '/admin/reports/stats',
    adminResolve: (id: string) => `/admin/reports/${id}`,
  },

  polls: {
    get:    (discussionId: string) => `/discussions/${discussionId}/poll`,
    create: (discussionId: string) => `/discussions/${discussionId}/poll`,
    vote:   (discussionId: string) => `/discussions/${discussionId}/poll/vote`,
    delete: (discussionId: string) => `/discussions/${discussionId}/poll`,
    voters: (discussionId: string) => `/discussions/${discussionId}/poll/voters`,
  },

  chat: {
    agents:          '/chat/agents',
    agent:           (id: string) => `/chat/agents/${id}`,
    categories:      '/chat/categories',
    conversations:   '/chat/conversations',
    createConv:      '/chat/conversations',
    messages:        (convId: string) => `/chat/conversations/${convId}/messages`,
    messagesByIds:   (convId: string) => `/chat/conversations/${convId}/messages/by-ids`,
    searchMessages:    (convId: string) => `/chat/conversations/${convId}/messages/search`,
    clearMessages:     (convId: string) => `/chat/conversations/${convId}/messages/all`,
    deleteMessage:     (convId: string, msgId: string) => `/chat/conversations/${convId}/messages/${msgId}`,
    unlockOrder:       (convId: string, msgId: string) => `/chat/conversations/${convId}/messages/${msgId}/unlock/order`,
    unlockFree:        (convId: string, msgId: string) => `/chat/conversations/${convId}/messages/${msgId}/unlock/free`,
    unlockMessage:     (convId: string, msgId: string) => `/chat/conversations/${convId}/messages/${msgId}/unlock`,
    unlockInvoice:     (convId: string, msgId: string) => `/chat/conversations/${convId}/messages/${msgId}/invoice/download`,
    unlockInvoices:    '/chat/unlock-invoices',
    unlockInvoiceDownload: (invoiceId: string) => `/chat/unlock-invoices/${invoiceId}/download`,
  },

  payments: {
    plans:     '/payments/plans',
    subscribe: '/payments/subscribe',
    portal:    '/payments/portal',
  },

  monetization: {
    adminSettings:      '/admin/monetization/settings',
    adminReport:        '/admin/monetization/report',
    adminPayoutSummary: '/admin/monetization/payout-summary',
    adminPayouts:       '/admin/monetization/payouts',
    adminPayout:        (id: string) => `/admin/monetization/payouts/${id}`,
    adminPayoutsExport: '/admin/monetization/payouts/export',
    adminRagEarnings:   '/admin/monetization/rag-earnings',
    adminWebhookEarnings: '/admin/monetization/webhook-earnings',
    developerPayouts:   '/developer/payouts',
    adminBankDetails:   (developerId: string) => `/admin/monetization/developers/${developerId}/bank-details`,
  },

  legal: {
    adminList:   '/admin/legal-documents',
    adminUpload: (type: string) => `/admin/legal-documents/${type}`,
    adminDelete: (type: string) => `/admin/legal-documents/${type}`,
    list:        '/legal-documents',
    download:    (type: string) => `/legal-documents/${type}/download`,
    agree:       '/legal-documents/agree',
  },

  support: {
    submit:      '/support/tickets',
    adminList:   '/admin/support/tickets',
    adminUpdate: (id: string) => `/admin/support/tickets/${id}`,
    adminStats:  '/admin/support/stats',
  },

  sse: {
    connect: '/sse',
    stats:   '/sse/stats',
  },

} as const
